import { useState, useEffect } from 'react'
import { Plus, X } from 'lucide-react'

const capitalizeFirstLetter = (text: string): string => {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1)
}

interface ObservationSection {
  id: string
  title: string
  content: string
}

interface MechanicObservationsSectionsProps {
  readonly value?: string
  readonly onChange?: (value: string) => void
  readonly onBlur?: () => void
  readonly disabled?: boolean
}

/**
 * Componente para seções dinâmicas de observações do mecânico
 * Formato: "=== TÍTULO ===\nConteúdo\n\n=== TÍTULO2 ===\nConteúdo2"
 */
export function MechanicObservationsSections({
  value = '',
  onChange,
  onBlur,
  disabled = false,
}: Readonly<MechanicObservationsSectionsProps>) {
  const [sections, setSections] = useState<ObservationSection[]>([])

  // Parse value into sections on mount and when value changes externally
  useEffect(() => {
    if (value && value.trim()) {
      const parsed = parseObservations(value)
      if (parsed.length > 0) {
        setSections(parsed)
      } else {
        // If parsing failed, create default sections
        setSections([
          { id: '1', title: 'Prioridade', content: '' },
          { id: '2', title: 'Informações Adicionais', content: '' },
        ])
      }
    } else {
      // Default sections: Prioridade and Informações Adicionais
      setSections([
        { id: '1', title: 'Prioridade', content: '' },
        { id: '2', title: 'Informações Adicionais', content: '' },
      ])
    }
  }, [value])

  const parseObservations = (text: string): ObservationSection[] => {
    if (!text) return []
    
    const sections: ObservationSection[] = []
    const lines = text.split('\n')
    let currentTitle = ''
    let currentContent: string[] = []
    let sectionId = 1

    lines.forEach((line) => {
      const titleMatch = line.match(/^=== (.+?) ===$/)
      if (titleMatch) {
        // Save previous section
        if (currentTitle || currentContent.length > 0) {
          sections.push({
            id: String(sectionId++),
            title: currentTitle,
            content: currentContent.join('\n').trim(),
          })
        }
        currentTitle = titleMatch[1]
        currentContent = []
      } else if (line.trim() === '' && currentTitle) {
        // Empty line - continue
      } else if (currentTitle) {
        currentContent.push(line)
      } else if (line.trim()) {
        // Content without title - add to current or create default
        if (sections.length === 0) {
          sections.push({
            id: String(sectionId++),
            title: '',
            content: line,
          })
        } else {
          currentContent.push(line)
        }
      }
    })

    // Save last section
    if (currentTitle || currentContent.length > 0) {
      sections.push({
        id: String(sectionId++),
        title: currentTitle,
        content: currentContent.join('\n').trim(),
      })
    }

    return sections.length > 0 ? sections : [{ id: '1', title: '', content: '' }]
  }

  const serializeObservations = (sections: ObservationSection[]): string => {
    return sections
      .filter(s => s.title.trim() || s.content.trim())
      .map(s => {
        if (s.title.trim()) {
          return `=== ${s.title} ===\n${s.content}`
        }
        return s.content
      })
      .join('\n\n')
  }

  const handleSectionChange = (id: string, field: 'title' | 'content', newValue: string) => {
    const updated = sections.map(s => {
      if (s.id === id) {
        const capitalized = field === 'title' 
          ? capitalizeFirstLetter(newValue)
          : capitalizeFirstLetter(newValue)
        return { ...s, [field]: capitalized }
      }
      return s
    })
    setSections(updated)
    if (onChange) {
      onChange(serializeObservations(updated))
    }
  }

  const handleAddSection = () => {
    const newSection: ObservationSection = {
      id: String(Date.now()),
      title: '',
      content: '',
    }
    const updated = [...sections, newSection]
    setSections(updated)
    if (onChange) {
      onChange(serializeObservations(updated))
    }
  }

  const handleRemoveSection = (id: string) => {
    if (sections.length <= 1) return // Keep at least one section
    const updated = sections.filter(s => s.id !== id)
    setSections(updated)
    if (onChange) {
      onChange(serializeObservations(updated))
    }
  }

  const handleBlur = () => {
    if (onBlur) {
      onBlur()
    }
  }

  return (
    <div className="space-y-4">
      {sections.map((section) => (
        <div key={section.id} className="space-y-2 p-4 border border-secondary-200 rounded-lg bg-white">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 space-y-2">
              <input
                type="text"
                value={section.title}
                onChange={(e) => handleSectionChange(section.id, 'title', e.target.value)}
                onBlur={handleBlur}
                disabled={disabled}
                placeholder={section.title || 'Título'}
                className={cn(
                  "w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm font-semibold text-secondary-900",
                  "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                  !section.title && "italic text-secondary-400"
                )}
              />
              <textarea
                value={section.content}
                onChange={(e) => handleSectionChange(section.id, 'content', e.target.value)}
                onBlur={handleBlur}
                disabled={disabled}
                placeholder={section.content || 'Observações'}
                rows={3}
                className={cn(
                  "w-full rounded-lg border border-secondary-200 bg-white px-3 py-2 text-sm text-secondary-900",
                  "focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100",
                  "disabled:opacity-50 disabled:cursor-not-allowed resize-none",
                  !section.content && "italic text-secondary-400"
                )}
              />
            </div>
            {sections.length > 1 && !disabled && (
              <button
                type="button"
                onClick={() => handleRemoveSection(section.id)}
                className="p-2 text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Remover secção"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ))}
      
      {!disabled && (
        <button
          type="button"
          onClick={handleAddSection}
          className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-secondary-300 rounded-lg text-sm font-semibold text-secondary-600 hover:border-primary-400 hover:text-primary-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Adicionar secção
        </button>
      )}
    </div>
  )
}

function cn(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

